import React, { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Spinner } from './Spinner';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

export const AvatarUpload = ({ url, onUpload, className = '' }) => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const uploadAvatar = async (event) => {
    try {
      setUploading(true);
      setError(null);

      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }

      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      
      // Basic validation
      if (!['jpg', 'jpeg', 'png', 'webp'].includes(fileExt.toLowerCase())) {
        throw new Error('Please upload a valid image file (JPG, PNG, WEBP).');
      }
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image must be less than 5MB.');
      }

      // Create a unique file path tied to the user's ID to satisfy RLS
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      onUpload(publicUrl);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={`flex flex-col items-center sm:items-start ${className}`}>
      <div className="flex items-center space-x-6">
        <div className="relative group">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-slate-100 border-2 border-slate-200 flex items-center justify-center">
            {uploading ? (
              <Spinner size="sm" />
            ) : url ? (
              <img src={url} alt="Profile Avatar" className="w-full h-full object-cover" />
            ) : (
              <SafeIcon icon={FiIcons.FiUser} className="text-3xl text-slate-400" />
            )}
          </div>
          
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-slate-900 border-2 border-white text-white flex items-center justify-center hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50"
            aria-label="Upload profile photo"
          >
            <SafeIcon icon={FiIcons.FiCamera} className="text-sm" />
          </button>
        </div>
        
        <div className="flex flex-col">
          <p className="text-sm font-medium text-slate-900">Profile photo</p>
          <p className="text-xs text-slate-500 mt-1">JPG, PNG or WEBP. Max 5MB.</p>
          {url && (
            <button 
              type="button" 
              onClick={() => onUpload(null)}
              className="text-xs text-red-600 font-medium hover:text-red-700 mt-2 text-left w-fit"
            >
              Remove photo
            </button>
          )}
        </div>
      </div>
      
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      
      <input
        type="file"
        ref={fileInputRef}
        onChange={uploadAvatar}
        accept="image/png, image/jpeg, image/webp"
        className="hidden"
      />
    </div>
  );
};